import renderIcon from 'icon';
import React, { useState } from 'react';
import { IoArrowBackCircleSharp } from 'react-icons/io5';

// Add these icon imports or create placeholder components
const ClockIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const TruckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"
    />
  </svg>
);

const StarIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

interface HeaderVendorProps {
  title: string;
  description: string;
  vendorBannerSrc?: string;
  handleBack: () => void;
  isLoading?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  seller?: {
    phoneNumber: string;
    address: {
      street: string;
      phoneNumber: string;
    };
    businessName: string;
    brandLogo: string;
    seller_logo?: string;
  };
}

const HeaderVendor: React.FC<HeaderVendorProps> = ({
  title,
  description,
  vendorBannerSrc,
  handleBack,
  seller,
  isLoading = false,
  searchQuery = '',
  onSearchChange
}) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Use seller_logo or brandLogo as fallback
  const bannerImage = vendorBannerSrc || seller?.seller_logo || seller?.brandLogo;

  const handleSearchIconClick = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchClose = () => {
    setIsSearchExpanded(false);
    setLocalSearchQuery('');
    onSearchChange?.('');
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
    onSearchChange?.(value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Search functionality is handled by onChange
  };

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-32 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between px-1">
        {renderIcon(IoArrowBackCircleSharp, {
          size: 24,
          className: ' text-black flex-shrink-0 cursor-pointer hover:text-gray-600 transition-colors',
          onClick: handleBack
        })}

        {/* Search Section */}
        <div className="flex items-center">
          {!isSearchExpanded ? (
            <button onClick={handleSearchIconClick} className="text-black hover:text-gray-600 transition-colors p-1">
              <SearchIcon />
            </button>
          ) : (
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  value={localSearchQuery}
                  onChange={handleSearchInputChange}
                  placeholder="Search products..."
                  className="w-48 sm:w-64 px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  autoFocus
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <SearchIcon />
                </div>
              </div>
              <button
                type="button"
                onClick={handleSearchClose}
                className="text-gray-500 hover:text-gray-700 transition-colors p-1">
                <CloseIcon />
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Header Section with Background Image */}
      <div className="relative">
        <div
          className="relative border rounded-lg border-[#F4F5F9] h-[140px] sm:h-[160px] overflow-hidden"
          style={{
            backgroundImage: bannerImage ? `url(${bannerImage})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}>
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>

          {/* All content overlay */}
          <div className="absolute inset-0 flex flex-col justify-between p-4 text-white">
            {/* Top section - title */}
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <h1 className="text-white font-semibold text-xl sm:text-2xl lg:text-3xl leading-tight">{title}</h1>{' '}
                <p>{seller?.address?.street || 'Address not available'}</p>
              </div>
            </div>

            {/* Bottom section - All other info */}
            <div className="space-y-4">
              {/* Rating and description */}
              {/* <div className="space-y-2">
                {description && <p className="text-sm text-gray-100 line-clamp-2">{description}</p>}
              </div> */}

              {/* Quick Info Section */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-200">
                  <ClockIcon />
                  <span>20-60 min</span>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-200">
                  <TruckIcon />
                  <span>from ₦600 delivery</span>
                </div>

                <div className="flex items-center gap-2 text-sm text-green-300">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span>Open now</span>
                </div>
              </div>
            </div>
          </div>

          {/* Fallback when no image */}
          {!bannerImage && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white opacity-30">
                <div className="w-12 h-12 mx-auto mb-2 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold">{title?.charAt(0) || 'V'}</span>
                </div>
                <p className="text-xs">Vendor</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-b border-gray-100"></div>
    </div>
  );
};

export default HeaderVendor;
